// @flow

import React, { type Element, useEffect, useState } from 'react'
import { Box, Container } from '@material-ui/core'
import type { Speciality } from '../types'
import StaticField from '../StaticField'
import SpecialitySelect from './SpecialitySelect'
import FieldButtons from '../FieldButtons'
import { grey } from '@material-ui/core/colors'

type Props = {|
  currentSpeciality: Speciality,
  specialityList: Array<Speciality>,
  currentGimTraining: boolean,
  updateProfile: (any) => Promise<boolean>,
  error: Array<string>,
|}

function SpecialityContainer({
  currentSpeciality,
  specialityList,
  currentGimTraining,
  updateProfile,
  error,
}: Props): Element<typeof React.Fragment> {
  const [selectedSpeciality, setSelectedSpeciality] = useState(currentSpeciality)
  const [newSpeciality, setNewSpeciality] = useState(currentSpeciality)
  const [selectedGimTraining, setSelectedGimTraining] = useState(currentGimTraining)
  const [newGimTraining, setNewGimTraining] = useState(currentGimTraining)
  const [updateDisabled, setUpdateDisabled] = useState(true)
  const [errorMessage, setErrorMessage] = useState(error.toString())
  const isLoading = false // will be converted to state once API is integrated
  const [editMode, setEditMode] = useState(false)
  const enableEditMode = () => setEditMode(true)
  const disableEditMode = () => setEditMode(false)

  const fieldContent: string =
    selectedSpeciality && selectedSpeciality.title ? selectedSpeciality.title : 'None'

  function handleCancel(): void {
    setNewSpeciality(selectedSpeciality)
    setNewGimTraining(selectedGimTraining)
    setErrorMessage('')
    disableEditMode()
    setUpdateDisabled(true)
  }

  async function handleUpdate(): Promise<void> {
    const payload = {
      speciality_id: newSpeciality.id,
      gim_extended_training: newGimTraining,
    }
    const success: boolean = await updateProfile(payload)

    if (success) {
      setSelectedSpeciality(newSpeciality)
      setSelectedGimTraining(newGimTraining)
      setErrorMessage('')
      disableEditMode()
      setUpdateDisabled(true)
    }
  }

  useEffect(() => {
    if (newSpeciality !== selectedSpeciality || newGimTraining !== selectedGimTraining)
      setUpdateDisabled(false)
    else setUpdateDisabled(true)
  }, [newSpeciality, newGimTraining, selectedSpeciality, selectedGimTraining])

  useEffect(() => {
    setErrorMessage(error.toString())
  }, [error])

  return (
    <Container maxWidth="md" disableGutters>
      {editMode ? (
        <Box borderBottom={1} color={grey[300]} pb={2}>
          <SpecialitySelect
            speciality={newSpeciality}
            setSpeciality={setNewSpeciality}
            specialityList={specialityList}
            gimTraining={newGimTraining}
            setGimTraining={setNewGimTraining}
            error={errorMessage.toString()}
          />
          <FieldButtons
            handleCancel={handleCancel}
            handleUpdate={handleUpdate}
            updateDisabled={updateDisabled}
            isLoading={isLoading}
          />
        </Box>
      ) : (
        <StaticField title="Speciality" content={fieldContent} handleEdit={enableEditMode} />
      )}
    </Container>
  )
}

export default SpecialityContainer
